import type { Point, StrokePoint, Stroke, ToolType, AppMode } from '../../types';
import type {
  DiagramTool,
  ShapeStyle,
  ConnectorStyle,
  TextStyle,
  ArrowHead,
} from '../diagram/state/types';
import { DEFAULT_TEXT_STYLE, SHAPE_PRESETS } from '../diagram/state/types';
import { Shape } from '../diagram/shapes/Shape';
import { Connector } from '../diagram/shapes/Connector';
import { TextElement } from '../diagram/shapes/TextElement';
import { ShapeRecognizer } from '../diagram/tools/ShapeRecognizer';
import { DiagramTextEditor } from '../diagram/controllers/DiagramTextEditor';
import { DiagramConnectorManager } from '../diagram/controllers/DiagramConnectorManager';
import { DiagramClipboardManager } from '../diagram/controllers/DiagramClipboardManager';
import { DiagramStore } from '../diagram/state/DiagramStore';
import {
  deserializeDiagramElements,
  serializeDiagramElements,
  serializeDiagramElementsJson,
  type DiagramDeserializedElements,
} from '../diagram/state/DiagramElementSerializer';
import {
  filterOutSelection,
} from '../diagram/state/diagramSelection';
import { getResizeCursor } from '../diagram/shapes/handleUtils';
import { getStrokeHandleAtPoint } from '../draw/strokeResize';
import { CanvasInputController } from '../../input/CanvasInputController';
import { ViewportCanvasBase } from '../../utils/ViewportCanvasBase';
import type { ViewportSnapshot } from '../../utils/ViewportControllerBase';
import { getCanvasContext, resizeCanvas } from '../../utils/canvas';
import { renderGridAxis, renderGridLines } from '../../utils/grid';
import { drawStrokeOutline, getStrokeOutlinePoints } from '../draw/strokeUtils';
import {
  DrawShape,
  DrawRectangle,
  DrawEllipse,
  DrawDiamond,
  DrawTriangle,
  type DrawShapeStyle,
  type DrawShapeType,
} from '../draw/shapes';
import { UnifiedStore } from './UnifiedStore';
import { UnifiedViewportController } from './UnifiedViewportController';
import { UnifiedRenderPipeline } from './UnifiedRenderPipeline';
import { UnifiedSelectionManager } from './UnifiedSelectionManager';
import { UnifiedDragController } from './UnifiedDragController';
import { UnifiedHistoryController } from './UnifiedHistoryController';
import { UnifiedExportManager } from './UnifiedExportManager';
import {
  DIAGRAM_SHORTCUTS,
  handleShortcutActions,
  isMacPlatform,
  isTextInputTarget,
} from '../../input/shortcuts';

type UnifiedExportOptions = { transparent?: boolean; scale?: number; selectionOnly?: boolean };

// Merged tool type: draw + diagram tools
type UnifiedTool = ToolType | DiagramTool | 'connector' | 'draw' | 'cylinder';

// Serializable text element data
type TextElementData = {
  id?: string;
  position: Point;
  text: string;
  textStyle: TextStyle;
  parentShapeId?: string;
  maxWidth?: number;
  autoResize?: boolean;
};
type DrawShapeData = {
  id: string;
  type: DrawShapeType;
  position: Point;
  size: { width: number; height: number };
  style: DrawShapeStyle;
};

const normalizeTextStyle = (style?: Partial<TextStyle>): TextStyle => ({
  ...DEFAULT_TEXT_STYLE,
  ...style,
});

const serializeTextElements = (textElements: TextElement[]): TextElementData[] =>
  textElements.map((t) => {
    const data: TextElementData = {
      id: t.id,
      position: { ...t.position },
      text: t.text,
      textStyle: { ...t.textStyle },
    };
    if (t.parentShapeId) data.parentShapeId = t.parentShapeId;
    if (t.maxWidth !== undefined) data.maxWidth = t.maxWidth;
    if (!t.autoResize) data.autoResize = false;
    return data;
  });

const deserializeDrawShapes = (items: DrawShapeData[] = []): DrawShape[] =>
  items.map((item) => {
    switch (item.type) {
      case 'rectangle': return DrawRectangle.fromJSON(item);
      case 'ellipse': return DrawEllipse.fromJSON(item);
      case 'diamond': return DrawDiamond.fromJSON(item);
      case 'triangle': return DrawTriangle.fromJSON(item);
    }
  });

export class UnifiedCanvas extends ViewportCanvasBase<UnifiedExportOptions, UnifiedExportOptions> {
  protected readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;

  private strokeCanvas: HTMLCanvasElement;
  private strokeCtx: CanvasRenderingContext2D;

  private readonly store: UnifiedStore;
  private readonly diagramStore: DiagramStore;
  private readonly recognizer: ShapeRecognizer;

  // Controllers
  protected readonly viewport: UnifiedViewportController;
  private readonly renderPipeline: UnifiedRenderPipeline;
  private readonly selectionManager: UnifiedSelectionManager;
  private readonly dragController: UnifiedDragController;
  private readonly historyController: UnifiedHistoryController;
  protected readonly exportManager: UnifiedExportManager;
  private readonly textEditor: DiagramTextEditor;
  private readonly connectorManager: DiagramConnectorManager;
  private readonly clipboardManager: DiagramClipboardManager;
  private readonly inputController: CanvasInputController;

  // Tool state
  private currentTool: UnifiedTool = 'pen';
  private activeMode: AppMode = 'draw';
  private shapeCreationMode: 'draw' | 'diagram' = 'draw';
  private currentColor = '#000000';
  private currentWidth = 8;
  private currentTextStyle: TextStyle = { ...DEFAULT_TEXT_STYLE };
  private currentStyle: Partial<ShapeStyle> = SHAPE_PRESETS['default'] ?? {};
  private currentConnectorStyle: Partial<ConnectorStyle> = {};

  // Drawing state (pen/eraser strokes)
  private isDrawing = false;
  private currentStroke: Stroke | null = null;
  private strokeEraseMode = true;
  private strokesToDelete: Set<number> = new Set();
  private elementsToDelete: Set<string> = new Set();

  // Diagram free-draw (shape recognition)
  private isRecognizing = false;
  private drawPoints: Point[] = [];

  // Draw shape creation
  private isCreatingDrawShape = false;
  private drawShapeCreationStart: Point | null = null;
  private previewDrawShape: DrawShape | null = null;

  // Placeholder (diagram empty state hint)
  private placeholderOpacity = 1;
  private placeholderFadeTimer: number | null = null;

  // Tool change callback
  private onBrushSizeChangeCallback: ((size: number) => void) | null = null;

  private stateLoaded = false;

  protected getPanModeCursor(enabled: boolean): string {
    if (enabled) return 'grab';
    if (this.currentTool === 'select') return 'default';
    if (this.currentTool === 'text') return 'text';
    return 'crosshair';
  }

  constructor(canvas: HTMLCanvasElement, unifiedStore: UnifiedStore) {
    super();
    this.canvas = canvas;
    this.ctx = getCanvasContext(canvas);
    this.store = unifiedStore;
    this.diagramStore = new DiagramStore(unifiedStore);
    this.recognizer = new ShapeRecognizer();
    this.currentTextStyle = { ...DEFAULT_TEXT_STYLE, color: this.currentColor };

    // Offscreen canvas for stroke compositing
    this.strokeCanvas = document.createElement('canvas');
    this.strokeCtx = getCanvasContext(this.strokeCanvas, 'Failed to get stroke canvas 2D context');

    // Viewport
    this.viewport = new UnifiedViewportController(
      {
        store: this.store,
        getCurrentTextStyle: (): TextStyle => this.currentTextStyle,
        getCurrentStyle: (): Partial<ShapeStyle> => this.currentStyle,
        setCurrentStyle: (s): void => { this.currentStyle = s; },
        getCurrentConnectorStyle: (): Partial<ConnectorStyle> => this.currentConnectorStyle,
        setCurrentConnectorStyle: (s): void => { this.currentConnectorStyle = s; },
        render: (): void => this.render(),
        notifyChange: (): void => this.notifyChange(),
      },
    );

    // Text editor
    this.textEditor = new DiagramTextEditor({
      canvas: this.canvas,
      ctx: this.ctx,
      getScale: (): number => this.getScale(),
      getPan: (): { panX: number; panY: number } => ({ panX: this.panX, panY: this.panY }),
      isInverted: (): boolean => this.inverted,
      render: (): void => this.render(),
      notifyChange: (): void => this.commitChange(),
      updatePlaceholder: (): void => this.updatePlaceholderVisibility(),
      removeTextElement: (element): void => this.removeTextElement(element),
    });

    // History
    this.historyController = new UnifiedHistoryController({
      getSnapshot: (): string => this.getStateSnapshot(),
      restoreSnapshot: (snapshot): void => this.restoreStateSnapshot(snapshot),
      onChange: (): void => this.onChangeCallback?.(),
    });

    // Selection
    this.selectionManager = new UnifiedSelectionManager({
      store: this.store,
      ctx: this.ctx,
      getInverted: (): boolean => this.inverted,
      getScale: (): number => this.scale,
      render: (): void => this.render(),
    });

    // Connector manager (operates on diagramStore which wraps unifiedStore)
    this.connectorManager = new DiagramConnectorManager({
      store: this.diagramStore,
      ctx: this.ctx,
      getGridSize: (): number => this.viewport.getGridSize(),
      getPanScale: (): { panX: number; panY: number; scale: number } => this.viewport.getPanScale(),
      getInverted: (): boolean => this.inverted,
      getCurrentConnectorStyle: (): Partial<ConnectorStyle> => this.currentConnectorStyle,
      snapToGrid: (point): Point => this.snapToGrid(point),
      render: (): void => this.render(),
      notifyChange: (): void => this.notifyChange(),
      notifySelectionChange: (): void => this.selectionManager.notifySelectionChange(),
      selectElement: (element): void => this.selectionManager.selectElement(element),
      clearSelection: (): void => this.selectionManager.clearSelection(),
      fadeOutPlaceholder: (): void => this.fadeOutPlaceholder(),
    });

    // Drag controller
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    this.dragController = new UnifiedDragController({
      store: this.store,
      ctx: this.ctx,
      get snapEnabled(): boolean { return self.snapEnabled; },
      snapToGrid: (point): Point => this.snapToGrid(point),
      connectorManager: this.connectorManager,
      selectionManager: this.selectionManager,
      render: (): void => this.render(),
      notifyChange: (): void => this.notifyChange(),
    });

    // Clipboard manager
    this.clipboardManager = new DiagramClipboardManager({
      store: this.diagramStore,
      deserializeElements: (data): DiagramDeserializedElements =>
        deserializeDiagramElements(data, this.recognizer),
      clearSelection: (): void => this.selectionManager.clearSelection(),
      selectElement: (element): void => this.selectionManager.selectElement(element),
      deleteSelected: (): void => this.deleteSelected(),
      fadeOutPlaceholder: (): void => this.fadeOutPlaceholder(),
      notifyChange: (): void => this.notifyChange(),
      render: (): void => this.render(),
    });

    // Render pipeline
    this.renderPipeline = new UnifiedRenderPipeline({
      canvas: this.canvas,
      ctx: this.ctx,
      strokeCanvas: this.strokeCanvas,
      strokeCtx: this.strokeCtx,
      store: this.store,
      getViewport: (): ViewportSnapshot => this.getViewportSnapshot(),
      getStrokes: (): Stroke[] => this.store.strokes,
      getCurrentStroke: (): Stroke | null => this.currentStroke,
      getPreviewShape: (): DrawShape | null => this.previewDrawShape,
      getStrokesToDelete: (): Set<number> => this.strokesToDelete,
      getElementsToDelete: (): Set<string> => this.elementsToDelete,
      getSelectedStrokeIds: (): Set<string> => this.selectionManager.getSelectedStrokeIds(),
      getBoxSelectionBounds: (): { x: number; y: number; width: number; height: number } | null => null,
      getPlaceholderOpacity: (): number => this.placeholderOpacity,
      getCurrentTool: (): string => this.currentTool,
      getSelectionBox: (): { active: boolean; start: Point | null; end: Point | null } => this.selectionManager.getSelectionBoxState(),
      drawGrid: (width, height, ctx): void => this.drawGrid(width, height, ctx),
      drawStrokeToLayer: (stroke, ctx): void => this.drawStrokeToLayer(stroke, ctx),
      isConnectorToolOrSelected: (): boolean =>
        this.currentTool === 'connector' || this.selectionManager.getSelectedConnector() !== null,
    });

    // Export manager
    this.exportManager = new UnifiedExportManager({
      store: this.store,
      getCanvas: (): HTMLCanvasElement => this.canvas,
      isInverted: (): boolean => this.inverted,
      drawStrokeToLayer: (stroke, ctx): void => this.drawStrokeToLayer(stroke, ctx),
      clearSelection: (): void => this.selectionManager.clearSelection(),
      selectById: (id): void => this.selectionManager.selectById(id),
      render: (): void => this.render(),
    });

    // Input
    this.inputController = new CanvasInputController(this.canvas, {
      pointerDown: this.handlePointerDown,
      pointerMove: this.handlePointerMove,
      pointerUp: this.handlePointerUp,
      pointerLeave: this.handlePointerUp,
      pointerCancel: this.handlePointerUp,
      doubleClick: this.handleDoubleClick,
      wheel: this.createWheelHandler(() => this.textEditor.updatePosition()),
      keyDown: this.handleKeyDown,
      keyUp: this.createPanKeyUpHandler(),
      contextMenu: (e): void => e.preventDefault(),
    });

    this.initCanvas();
    this.inputController.attach();
    this.historyController.ensureSnapshot(this.getStateSnapshot());
  }

  private initCanvas(): void {
    this.initCanvasBase();
    this.initResizeObserver((width, height, dpr): void => {
      resizeCanvas(this.canvas, this.ctx, width, height, dpr);
      resizeCanvas(this.strokeCanvas, this.strokeCtx, width, height, dpr);
      this.centerOnFirstResize(width, height, !this.stateLoaded);
      this.render();
    });
  }

  // --- Mode switching (toolbar swap only) ---
  setActiveMode(mode: AppMode): void {
    this.activeMode = mode;
    // Default tool per mode
    if (mode === 'draw') {
      this.setTool('pen');
    } else {
      this.setTool('select');
    }
  }

  getActiveMode(): AppMode {
    return this.activeMode;
  }

  // --- Pointer events ---
  private handlePointerDown = (e: PointerEvent): void => {
    const wasEditing = this.textEditor.isEditing();
    if (wasEditing) {
      this.textEditor.close();
    }

    if (this.shouldStartPan(e)) {
      e.preventDefault();
      this.startPan(e);
      return;
    }

    // Right-click → selection box
    if (e.button === 2) {
      e.preventDefault();
      this.canvas.setPointerCapture(e.pointerId);
      this.selectionManager.startSelectionBox(this.getWorldPoint(e));
      this.canvas.style.cursor = 'crosshair';
      return;
    }

    if (e.button !== 0) return;
    e.preventDefault();
    this.canvas.setPointerCapture(e.pointerId);

    const point = this.getWorldPoint(e);
    const tool = this.currentTool;

    // --- SELECT tool ---
    if (tool === 'select') {
      // Always box-selection on empty space; free-draw recognition is via the explicit 'draw' tool
      const result = this.selectionManager.handleSelectDown(point, e.shiftKey);
      if (result.handled) {
        this.dragController.startDrag(point, result.dragElement, result.resizeHandle);
      } else {
        this.selectionManager.startSelectionBox(point);
      }
      this.render();
      return;
    }

    // --- PEN / ERASER tool ---
    if (tool === 'pen' || tool === 'eraser') {
      this.selectionManager.clearSelection();
      this.saveState();
      this.isDrawing = true;

      if (tool === 'eraser' && this.strokeEraseMode) {
        this.strokesToDelete.clear();
        this.elementsToDelete.clear();
        this.checkEraserIntersection(point as StrokePoint);
        this.render();
        return;
      }

      const strokeColor = tool === 'eraser' ? '#000000' : this.currentColor;
      this.currentStroke = {
        id: crypto.randomUUID(),
        points: [point as StrokePoint],
        color: strokeColor,
        baseWidth: tool === 'eraser' ? this.currentWidth * 3 : this.currentWidth,
        tool: tool,
      };
      this.render();
      return;
    }

    // --- TEXT tool ---
    if (tool === 'text') {
      // Check resize handles on selected text elements first
      for (const t of this.store.textElements) {
        if (this.store.selectedElements.has(t.id)) {
          const handle = t.getHandleAtPoint(point, this.ctx, this.scale);
          if (handle) {
            this.dragController.startDrag(point, t, handle);
            this.render();
            return;
          }
        }
      }

      const existingText = this.getTextElementAtPoint(point);
      if (existingText) {
        // Single click selects only; double-click opens editor
        this.selectionManager.clearSelection();
        this.selectionManager.selectById(existingText.id);
        this.dragController.startDrag(point, existingText, null);
        this.render();
      } else if (wasEditing) {
        // Just closed the editor — don't immediately create another text box
        // Allow select/drag via handleSelectDown so user can reposition elements
        const result = this.selectionManager.handleSelectDown(point, e.shiftKey);
        if (result.handled) {
          this.dragController.startDrag(point, result.dragElement, result.resizeHandle);
        } else {
          this.selectionManager.clearSelection();
        }
        this.render();
      } else {
        this.createTextElement(point);
      }
      return;
    }

    // --- CONNECTOR tool ---
    if (tool === 'connector') {
      this.connectorManager.startConnector(point);
      return;
    }

    // --- DRAW tool (diagram free-draw for shape recognition) ---
    if (tool === 'draw') {
      this.isRecognizing = true;
      this.drawPoints = [point];
      return;
    }

    // --- SHAPE tools (draw mode shapes: rectangle, ellipse, diamond, triangle) ---
    if (this.isDrawShapeTool()) {
      const hadSelection = this.store.selectedElements.size > 0;

      // Check resize handles on any selected element first
      const shapeSelectResult = this.selectionManager.handleSelectDown(point, false);
      if (shapeSelectResult.handled && shapeSelectResult.resizeHandle) {
        this.dragController.startDrag(point, shapeSelectResult.dragElement!, shapeSelectResult.resizeHandle);
        this.render();
        return;
      }

      // Check any element at point — if already selected, drag; otherwise just select
      const hitElement = this.selectionManager.getElementAtPoint(point);
      if (hitElement) {
        const element = hitElement.kind === 'stroke' ? hitElement.stroke : hitElement.element;
        if (this.store.selectedElements.has(element.id)) {
          // Already selected → start drag
          this.dragController.startDrag(point, element, null);
        } else {
          // Not yet selected → select only (no drag)
          this.selectionManager.clearSelection();
          this.selectionManager.selectById(element.id);
        }
        this.render();
        return;
      }

      if (this.shapeCreationMode === 'diagram') {
        // Diagram mode: clicking off a selected shape deselects first;
        // only create a new shape if nothing was selected before this click
        if (hadSelection) {
          this.selectionManager.clearSelection();
        } else {
          this.createDiagramShape(point);
        }
      } else {
        // Draw mode: drag to create draw shape
        this.saveState();
        this.isCreatingDrawShape = true;
        const snappedStart = this.snapEnabled ? this.snapToGrid(point) : point;
        this.drawShapeCreationStart = snappedStart;
        this.previewDrawShape = this.createDrawShape(tool as DrawShapeType, snappedStart, { width: 0, height: 0 });
      }
      this.render();
      return;
    }

    // Diagram cylinder tool
    if (tool === 'cylinder') {
      const hadSelection = this.store.selectedElements.size > 0;

      // Check resize handles on selected elements first
      const cylSelectResult = this.selectionManager.handleSelectDown(point, false);
      if (cylSelectResult.handled && cylSelectResult.resizeHandle) {
        this.dragController.startDrag(point, cylSelectResult.dragElement!, cylSelectResult.resizeHandle);
        this.render();
        return;
      }

      const cylHit = this.selectionManager.getElementAtPoint(point);
      if (cylHit) {
        const element = cylHit.kind === 'stroke' ? cylHit.stroke : cylHit.element;
        this.selectionManager.clearSelection();
        this.selectionManager.selectById(element.id);
        this.dragController.startDrag(point, element, null);
      } else if (hadSelection) {
        this.selectionManager.clearSelection();
      } else {
        this.createDiagramShape(point);
      }
      this.render();
      return;
    }
  };

  private handlePointerMove = (e: PointerEvent): void => {
    if (this.isPanning && this.panStart) {
      this.updatePan(e);
      this.textEditor.updatePosition();
      this.render();
      return;
    }

    if (this.selectionManager.isSelectionBoxActive()) {
      this.selectionManager.updateSelectionBox(this.getWorldPoint(e));
      this.render();
      return;
    }

    const point = this.getWorldPoint(e);

    // Shape recognition free-draw
    if (this.isRecognizing && this.drawPoints.length > 0) {
      this.drawPoints.push(point);
      this.renderDrawingPreview();
      return;
    }

    // Draw shape creation preview
    if (this.isCreatingDrawShape && this.drawShapeCreationStart && this.previewDrawShape) {
      const currentPoint = this.snapEnabled ? this.snapToGrid(point) : point;
      const width = currentPoint.x - this.drawShapeCreationStart.x;
      const height = currentPoint.y - this.drawShapeCreationStart.y;
      if (width < 0) {
        this.previewDrawShape.position.x = currentPoint.x;
        this.previewDrawShape.size.width = Math.abs(width);
      } else {
        this.previewDrawShape.position.x = this.drawShapeCreationStart.x;
        this.previewDrawShape.size.width = width;
      }
      if (height < 0) {
        this.previewDrawShape.position.y = currentPoint.y;
        this.previewDrawShape.size.height = Math.abs(height);
      } else {
        this.previewDrawShape.position.y = this.drawShapeCreationStart.y;
        this.previewDrawShape.size.height = height;
      }
      this.render();
      return;
    }

    // Drag/resize
    if (this.dragController.isActive()) {
      this.dragController.updateDrag(point);
      return;
    }

    // Connector preview
    if (this.currentTool === 'connector' && this.connectorManager.hasActiveConnector()) {
      this.connectorManager.renderConnectorPreview(point);
      return;
    }

    // Stroke drawing
    if (this.isDrawing) {
      if (this.currentTool === 'eraser' && this.strokeEraseMode) {
        e.preventDefault();
        this.checkEraserIntersection(point as StrokePoint);
        this.render();
        return;
      }
      if (this.currentStroke) {
        e.preventDefault();
        const events = e.getCoalescedEvents?.() ?? [e];
        for (const event of events) {
          const eventPoint = this.getWorldPointFromEvent(event);
          const smoothed = this.smoothPoint(eventPoint, event.pointerType);
          if (smoothed) this.currentStroke.points.push(smoothed);
        }
        this.render();
      }
      return;
    }

    // Cursor updates when idle
    this.updateCursor(point);
  };

  private handlePointerUp = (e: PointerEvent): void => {
    this.canvas.releasePointerCapture(e.pointerId);

    if (this.isPanning) {
      this.stopPan();
      return;
    }

    // Selection box end
    if (this.selectionManager.isSelectionBoxActive()) {
      this.selectionManager.finishSelectionBox();
      return;
    }

    const point = this.getWorldPoint(e);

    // Shape recognition finish
    if (this.isRecognizing) {
      this.finishRecognition();
      this.isRecognizing = false;
      this.render();
      return;
    }

    // Draw shape creation finish
    if (this.isCreatingDrawShape && this.previewDrawShape) {
      if (this.previewDrawShape.size.width > 5 && this.previewDrawShape.size.height > 5) {
        this.store.drawShapes.push(this.previewDrawShape);
        this.selectionManager.selectById(this.previewDrawShape.id);
        this.commitChange();
      }
      this.isCreatingDrawShape = false;
      this.drawShapeCreationStart = null;
      this.previewDrawShape = null;
      this.render();
      return;
    }

    // Connector finish
    if (this.currentTool === 'connector' && this.connectorManager.hasActiveConnector()) {
      this.connectorManager.finishConnector(point);
    }

    // Drag finish
    this.dragController.finishDrag();

    // Stroke finish
    if (this.isDrawing) {
      this.isDrawing = false;

      if (this.currentTool === 'eraser' && this.strokeEraseMode) {
        const hasStrokes = this.strokesToDelete.size > 0;
        const hasElements = this.elementsToDelete.size > 0;
        if (hasStrokes) {
          const indices = Array.from(this.strokesToDelete).sort((a, b) => b - a);
          for (const i of indices) {
            this.store.strokes.splice(i, 1);
          }
          this.strokesToDelete.clear();
        }
        if (hasElements) {
          const ids = this.elementsToDelete;
          const drawShapeIds = new Set(this.store.drawShapes.filter(d => ids.has(d.id)).map(d => d.id));
          this.store.shapes = this.store.shapes.filter(s => !ids.has(s.id));
          this.store.drawShapes = this.store.drawShapes.filter(d => !ids.has(d.id));
          this.store.textElements = this.store.textElements.filter(t => !ids.has(t.id) && (!t.parentShapeId || !drawShapeIds.has(t.parentShapeId)));
          this.store.connectors = this.store.connectors.filter(c => !ids.has(c.id));
          this.elementsToDelete.clear();
        }
        if (hasStrokes || hasElements) {
          this.updatePlaceholderVisibility();
          this.commitChange();
        }
        return;
      }

      if (this.currentStroke && this.currentStroke.points.length > 0) {
        this.store.strokes.push(this.currentStroke);
      }
      this.currentStroke = null;
      this.commitChange();
      return;
    }

    this.render();
  };

  private handleDoubleClick = (e: MouseEvent): void => {
    const point = this.getWorldPoint(e as unknown as PointerEvent);

    // Double-click text → edit
    const textHit = this.getTextElementAtPoint(point);
    if (textHit) {
      this.saveState();
      this.selectionManager.selectById(textHit.id);
      this.textEditor.editElement(textHit);
      return;
    }

    // Double-click draw shape → create/edit bound text
    const drawShapeHit = this.getDrawShapeAtPoint(point);
    if (drawShapeHit) {
      const existingText = this.store.textElements.find(t => t.parentShapeId === drawShapeHit.id);
      if (existingText) {
        this.saveState();
        this.selectionManager.selectById(existingText.id);
        this.textEditor.editElement(existingText);
        return;
      }
      this.saveState();
      const center = drawShapeHit.getCenter();
      const textElement = new TextElement(center, '', { ...this.currentTextStyle });
      textElement.parentShapeId = drawShapeHit.id;
      this.store.textElements.push(textElement);
      this.selectionManager.selectById(textElement.id);
      this.textEditor.editElement(textElement);
      this.commitChange();
      return;
    }

    // Double-click diagram element → edit text
    const diagramHit = this.getDiagramElementAtPoint(point);
    if (diagramHit) {
      this.textEditor.editElement(diagramHit);
      return;
    }

    // Double-click empty → create text (only in select/text tools)
    if (this.currentTool === 'select' || this.currentTool === 'text') {
      this.createTextElement(point);
    }
  };

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (isTextInputTarget(e.target) || this.textEditor.isEditing()) return;

    const isMac = isMacPlatform();

    const handleUndo = (): void => this.undo();
    const handleRedo = (): void => this.redo();
    const handleDelete = (): void => this.deleteSelected();
    const handleCopy = (): void => this.clipboardManager.copySelected();
    const handleCut = (): void => this.clipboardManager.cutSelected();
    const handlePaste = (): void => this.clipboardManager.paste();
    const handleDuplicate = (): void => this.clipboardManager.duplicateSelected();
    const handleSelectAll = (): void => this.selectionManager.selectAll();
    const handleClearSelection = (): void => {
      this.selectionManager.clearSelection();
      this.render();
    };

    // Use diagram shortcuts as the superset (includes delete, copy, paste, etc.)
    if (
      handleShortcutActions(
        e,
        [
          { shortcut: DIAGRAM_SHORTCUTS.undo, action: handleUndo, preventDefault: true },
          { shortcut: DIAGRAM_SHORTCUTS.redo, action: handleRedo, preventDefault: true },
          { shortcut: DIAGRAM_SHORTCUTS.delete, action: handleDelete, preventDefault: true },
          { shortcut: DIAGRAM_SHORTCUTS.copy, action: handleCopy, preventDefault: true },
          { shortcut: DIAGRAM_SHORTCUTS.cut, action: handleCut, preventDefault: true },
          { shortcut: DIAGRAM_SHORTCUTS.paste, action: handlePaste, preventDefault: true },
          { shortcut: DIAGRAM_SHORTCUTS.duplicate, action: handleDuplicate, preventDefault: true },
          { shortcut: DIAGRAM_SHORTCUTS.selectAll, action: handleSelectAll, preventDefault: true },
          { shortcut: DIAGRAM_SHORTCUTS.clearSelection, action: handleClearSelection },
        ],
        isMac,
      )
    ) {
      return;
    }

    const handleResetView = (): void => this.resetView();
    if (this.handlePanZoomShortcuts(e, DIAGRAM_SHORTCUTS, isMac, { onReset: handleResetView })) {
      return;
    }
  };

  // --- Helper methods ---

  private getWorldPointFromEvent(e: PointerEvent | MouseEvent): StrokePoint {
    const p = this.getWorldPoint(e as PointerEvent);
    return { x: p.x, y: p.y, pressure: 'pressure' in e ? (e as PointerEvent).pressure || 0.5 : 0.5 };
  }

  private getTextElementAtPoint(point: Point): TextElement | null {
    for (let i = this.store.textElements.length - 1; i >= 0; i--) {
      if (this.store.textElements[i]!.containsPoint(point, this.ctx)) return this.store.textElements[i]!;
    }
    return null;
  }

  private getDrawShapeAtPoint(point: Point): DrawShape | null {
    for (let i = this.store.drawShapes.length - 1; i >= 0; i--) {
      if (this.store.drawShapes[i]!.containsPoint(point)) return this.store.drawShapes[i]!;
    }
    return null;
  }

  private getDiagramElementAtPoint(point: Point): Shape | Connector | TextElement | null {
    for (let i = this.store.textElements.length - 1; i >= 0; i--) {
      if (this.store.textElements[i]!.containsPoint(point, this.ctx)) return this.store.textElements[i]!;
    }
    for (let i = this.store.connectors.length - 1; i >= 0; i--) {
      if (this.store.connectors[i]!.containsPoint(point)) return this.store.connectors[i]!;
    }
    for (let i = this.store.shapes.length - 1; i >= 0; i--) {
      if (this.store.shapes[i]!.containsPoint(point)) return this.store.shapes[i]!;
    }
    return null;
  }

  private isDrawShapeTool(): boolean {
    return this.currentTool === 'rectangle' || this.currentTool === 'ellipse' ||
           this.currentTool === 'diamond' || this.currentTool === 'triangle';
  }

  private createDrawShape(type: DrawShapeType, position: Point, size: { width: number; height: number }): DrawShape {
    const id = `shape-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const style: DrawShapeStyle = {
      fill: 'transparent',
      stroke: this.currentColor,
      strokeWidth: this.currentWidth,
      opacity: 1,
      cornerRadius: 4,
    };
    switch (type) {
      case 'rectangle': return new DrawRectangle(id, position, size, style);
      case 'ellipse': return new DrawEllipse(id, position, size, style);
      case 'diamond': return new DrawDiamond(id, position, size, style);
      case 'triangle': return new DrawTriangle(id, position, size, style);
    }
  }

  private createDiagramShape(point: Point): void {
    const snappedPoint = this.snapToGrid(point);
    const defaultSize = this.snapEnabled
      ? { width: this.gridSize * 6, height: this.gridSize * 4 }
      : { width: 140, height: 90 };
    const shape = this.recognizer.createShapeAtPosition(
      this.currentTool as 'rectangle' | 'ellipse' | 'diamond' | 'cylinder' | 'triangle',
      snappedPoint,
      defaultSize,
      this.currentStyle,
    );
    this.store.shapes.push(shape);
    this.fadeOutPlaceholder();
    this.selectionManager.clearSelection();
    this.selectionManager.selectById(shape.id);
    this.notifyChange();
    this.render();
  }

  private createTextElement(point: Point): void {
    const textColor = this.inverted ? '#ffffff' : '#000000';
    const position = this.snapEnabled ? this.snapToGrid(point) : point;
    const padding = 8;
    const adjustedPosition = this.shapeCreationMode === 'draw'
      ? { x: position.x - padding, y: position.y - padding }
      : { x: position.x, y: position.y };

    const style = { ...this.currentTextStyle };
    if (this.shapeCreationMode === 'diagram') {
      style.color = textColor;
    }

    const textElement = new TextElement(adjustedPosition, '', style);

    // Check if inside a draw shape → attach
    if (this.shapeCreationMode === 'draw') {
      const ds = this.getDrawShapeAtPoint(point);
      if (ds) textElement.parentShapeId = ds.id;
    }

    this.store.textElements.push(textElement);
    this.selectionManager.clearSelection();
    this.selectionManager.selectById(textElement.id);
    this.fadeOutPlaceholder();
    this.textEditor.editElement(textElement);
    this.notifyChange();
  }

  private finishRecognition(): void {
    if (this.drawPoints.length < 3) {
      this.drawPoints = [];
      return;
    }
    const result = this.recognizer.recognize(this.drawPoints, this.currentStyle);
    if (result.shape) {
      if (result.shape instanceof Shape) {
        if (this.snapEnabled) {
          const snappedPos = this.snapToGrid(result.shape.position);
          result.shape.position.x = snappedPos.x;
          result.shape.position.y = snappedPos.y;
          result.shape.size.width = Math.round(result.shape.size.width / this.gridSize) * this.gridSize;
          result.shape.size.height = Math.round(result.shape.size.height / this.gridSize) * this.gridSize;
          if (result.shape.size.width < this.gridSize) result.shape.size.width = this.gridSize;
          if (result.shape.size.height < this.gridSize) result.shape.size.height = this.gridSize;
        }
        this.store.shapes.push(result.shape);
        this.fadeOutPlaceholder();
        this.selectionManager.clearSelection();
        this.selectionManager.selectById(result.shape.id);
      } else if (result.shape instanceof Connector) {
        const dx = result.shape.endPoint.x - result.shape.startPoint.x;
        const dy = result.shape.endPoint.y - result.shape.startPoint.y;
        if (Math.hypot(dx, dy) >= this.gridSize * 2) {
          result.shape.style = { ...result.shape.style, ...this.currentConnectorStyle };
          if (this.snapEnabled) {
            result.shape.startPoint = this.snapToGrid(result.shape.startPoint);
            result.shape.endPoint = this.snapToGrid(result.shape.endPoint);
          }
          this.store.connectors.push(result.shape);
          this.fadeOutPlaceholder();
          this.selectionManager.clearSelection();
          this.selectionManager.selectById(result.shape.id);
        }
      }
      this.notifyChange();
    }
    this.drawPoints = [];
  }

  private renderDrawingPreview(): void {
    this.render();
    if (this.drawPoints.length < 2) return;
    this.ctx.save();
    this.ctx.translate(this.panX, this.panY);
    this.ctx.scale(this.scale, this.scale);
    this.ctx.strokeStyle = this.inverted ? '#60a5fa' : '#2563eb';
    this.ctx.lineWidth = 2 / this.scale;
    this.ctx.setLineDash([4 / this.scale, 4 / this.scale]);
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    this.ctx.beginPath();
    this.ctx.moveTo(this.drawPoints[0]!.x, this.drawPoints[0]!.y);
    for (let i = 1; i < this.drawPoints.length; i++) {
      this.ctx.lineTo(this.drawPoints[i]!.x, this.drawPoints[i]!.y);
    }
    this.ctx.stroke();
    this.ctx.restore();
  }

  private smoothPoint(point: StrokePoint, pointerType: string): StrokePoint | null {
    if (!this.currentStroke) return point;
    if (pointerType === 'pen' || pointerType === 'touch') return point;
    const points = this.currentStroke.points;
    if (points.length === 0) return point;
    const last = points[points.length - 1]!;
    const dx = point.x - last.x;
    const dy = point.y - last.y;
    if (Math.sqrt(dx * dx + dy * dy) < 1.5) return null;
    return point;
  }

  private checkEraserIntersection(eraserPoint: StrokePoint): void {
    const eraserRadius = (this.currentWidth * 3) / 2;
    // Check strokes
    for (let i = 0; i < this.store.strokes.length; i++) {
      const stroke = this.store.strokes[i]!;
      if (stroke.tool === 'eraser') continue;
      for (const p of stroke.points) {
        const dx = eraserPoint.x - p.x;
        const dy = eraserPoint.y - p.y;
        if (Math.sqrt(dx * dx + dy * dy) < eraserRadius + stroke.baseWidth / 2) {
          this.strokesToDelete.add(i);
          break;
        }
      }
    }
    // Check diagram shapes
    for (const shape of this.store.shapes) {
      if (shape.containsPoint(eraserPoint)) this.elementsToDelete.add(shape.id);
    }
    // Check draw shapes
    for (const ds of this.store.drawShapes) {
      if (ds.containsPoint(eraserPoint)) this.elementsToDelete.add(ds.id);
    }
    // Check text elements
    for (const t of this.store.textElements) {
      if (t.containsPoint(eraserPoint, this.ctx)) this.elementsToDelete.add(t.id);
    }
    // Check connectors
    for (const c of this.store.connectors) {
      if (c.containsPoint(eraserPoint)) this.elementsToDelete.add(c.id);
    }
  }

  private getSelectedHandleCursor(point: Point): string | null {
    const sel = this.store.selectedElements;
    for (const s of this.store.shapes) {
      if (sel.has(s.id)) {
        const h = s.getHandleAtPoint(point, this.scale);
        if (h) return getResizeCursor(h);
      }
    }
    for (const t of this.store.textElements) {
      if (sel.has(t.id)) {
        const h = t.getHandleAtPoint(point, this.ctx, this.scale);
        if (h) return getResizeCursor(h);
      }
    }
    for (const c of this.store.connectors) {
      if (sel.has(c.id)) {
        const h = c.getHandleAtPoint(point, this.scale);
        if (h) return 'grab';
      }
    }
    for (const d of this.store.drawShapes) {
      if (sel.has(d.id) && d.selected) {
        const h = d.getHandleAtPoint(point, this.scale);
        if (h) return getResizeCursor(h);
      }
    }
    for (const stroke of this.store.strokes) {
      if (sel.has(stroke.id)) {
        const h = getStrokeHandleAtPoint(stroke, point, this.scale);
        if (h) return getResizeCursor(h);
      }
    }
    return null;
  }

  private updateCursor(point: Point): void {
    const tool = this.currentTool;
    if (tool === 'pen' || tool === 'eraser') {
      this.canvas.style.cursor = 'crosshair';
      return;
    }

    // All selectable modes share handle cursor detection
    const handleCursor = this.getSelectedHandleCursor(point);
    if (handleCursor) { this.canvas.style.cursor = handleCursor; return; }

    if (tool === 'text') {
      const hitText = this.getTextElementAtPoint(point);
      this.canvas.style.cursor = hitText ? 'move' : 'text';
    } else {
      const hit = this.selectionManager.getElementAtPoint(point);
      if (hit) { this.canvas.style.cursor = 'move'; return; }
      this.canvas.style.cursor = tool === 'select'
        ? (this.shapeCreationMode === 'diagram' ? 'crosshair' : 'default')
        : 'crosshair';
    }
  }

  private drawGrid(width: number, height: number, ctx: CanvasRenderingContext2D): void {
    const viewport = { panX: this.panX, panY: this.panY, scale: this.scale };
    renderGridLines(ctx, {
      width, height, gridSize: this.gridSize,
      inverted: this.inverted, enabled: this.gridEnabled, viewport,
    });
    renderGridAxis(ctx, { inverted: this.inverted, enabled: this.gridEnabled, viewport });
  }

  private drawStrokeToLayer(stroke: Stroke, ctx: CanvasRenderingContext2D): void {
    const { points, color, tool } = stroke;
    const isEraser = tool === 'eraser';
    ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';
    if (points.length === 0) { ctx.globalCompositeOperation = 'source-over'; return; }
    const outlinePoints = getStrokeOutlinePoints(stroke);
    ctx.fillStyle = isEraser ? '#000000' : color;
    ctx.beginPath();
    if (!drawStrokeOutline(ctx, outlinePoints)) { ctx.globalCompositeOperation = 'source-over'; return; }
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
  }

  private removeTextElement(element: TextElement): void {
    const index = this.store.textElements.findIndex(t => t.id === element.id);
    if (index >= 0) this.store.textElements.splice(index, 1);
    this.store.selectedElements.delete(element.id);
    this.selectionManager.notifySelectionChange();
  }

  private updatePlaceholderVisibility(): void {
    const hasContent = this.store.shapes.length > 0 || this.store.connectors.length > 0 ||
      this.store.textElements.length > 0 || this.store.strokes.length > 0 || this.store.drawShapes.length > 0;
    this.placeholderOpacity = hasContent ? 0 : 1;
  }

  private fadeOutPlaceholder(): void {
    if (this.placeholderFadeTimer !== null || this.placeholderOpacity <= 0) return;
    const fadeStep = (): void => {
      this.placeholderOpacity -= 0.05;
      if (this.placeholderOpacity <= 0) {
        this.placeholderOpacity = 0;
        if (this.placeholderFadeTimer !== null) {
          cancelAnimationFrame(this.placeholderFadeTimer);
          this.placeholderFadeTimer = null;
        }
      } else {
        this.placeholderFadeTimer = requestAnimationFrame(fadeStep);
      }
      this.render();
    };
    this.placeholderFadeTimer = requestAnimationFrame(fadeStep);
  }

  // --- State ---
  private render(): void {
    this.renderPipeline.render();
  }

  private saveState(): void {
    this.historyController.recordSnapshot();
  }

  private commitChange(): void {
    this.render();
    this.notifyChange();
  }

  private notifyChange(): void {
    this.historyController.recordSnapshot();
    this.onChangeCallback?.();
  }

  private getStateSnapshot(): string {
    const diagramJson = serializeDiagramElementsJson(this.store.shapes, this.store.connectors, this.store.textElements);
    const diagramParsed = JSON.parse(diagramJson);
    return JSON.stringify({
      ...diagramParsed,
      strokes: this.store.strokes,
      drawShapes: this.store.drawShapes.map(s => s.toJSON()),
    });
  }

  private restoreStateSnapshot(snapshot: string): void {
    const data = JSON.parse(snapshot);
    const { shapes, connectors, textElements } = deserializeDiagramElements({
      shapes: data.shapes ?? [],
      connectors: data.connectors ?? [],
      textElements: data.textElements ?? [],
    }, this.recognizer);
    this.store.shapes = shapes;
    this.store.connectors = connectors;
    this.store.textElements = textElements;
    this.store.strokes = data.strokes ?? [];
    // Backfill missing stroke ids
    for (const s of this.store.strokes) {
      if (!s.id) s.id = crypto.randomUUID();
    }
    this.store.drawShapes = deserializeDrawShapes(data.drawShapes ?? []);
    this.store.selectedElements.clear();
    this.updatePlaceholderVisibility();
    this.connectorManager.syncConnectorEndpoints();
    this.render();
  }

  // --- Public API ---

  setShapeCreationMode(mode: 'draw' | 'diagram'): void {
    this.shapeCreationMode = mode;
  }

  getShapeCreationMode(): 'draw' | 'diagram' {
    return this.shapeCreationMode;
  }

  setTool(tool: UnifiedTool): void {
    this.currentTool = tool;
    // Clear selection and eraser state when switching tools
    this.selectionManager.clearSelection();
    this.elementsToDelete.clear();
    this.strokesToDelete.clear();
    this.canvas.style.cursor = this.getPanModeCursor(this.spacePressed || this.panModeEnabled);
    this.render();
  }

  getTool(): UnifiedTool {
    return this.currentTool;
  }

  setColor(color: string): void {
    this.currentColor = color;
  }

  getColor(): string {
    return this.currentColor;
  }

  setBrushSize(size: number): void {
    this.currentWidth = size;
    // Sync to currentStyle so new diagram shapes use this width
    this.currentStyle = { ...this.currentStyle, strokeWidth: size };
    // Update selected draw shapes' border width
    for (const d of this.store.drawShapes) {
      if (this.store.selectedElements.has(d.id)) {
        d.style.strokeWidth = size;
      }
    }
    // Update selected diagram shapes' border width
    for (const s of this.store.shapes) {
      if (this.store.selectedElements.has(s.id)) {
        s.style.strokeWidth = size;
      }
    }
    // Update selected connectors' stroke width
    for (const c of this.store.connectors) {
      if (this.store.selectedElements.has(c.id)) {
        c.style.strokeWidth = size;
      }
    }
    this.render();
  }

  getBrushSize(): number {
    return this.currentWidth;
  }

  setStrokeEraseMode(enabled: boolean): void {
    this.strokeEraseMode = enabled;
    this.strokesToDelete.clear();
    this.render();
  }

  getStrokeEraseMode(): boolean {
    return this.strokeEraseMode;
  }

  setStyle(style: Partial<ShapeStyle>): void {
    this.currentStyle = { ...this.currentStyle, ...style };
    for (const shape of this.store.shapes) {
      if (this.store.selectedElements.has(shape.id)) {
        shape.style = { ...shape.style, ...style };
        if (style.stroke) shape.textStyle = { ...shape.textStyle, color: style.stroke };
      }
    }
    this.render();
  }

  setConnectorStyle(style: Partial<ConnectorStyle>): void {
    this.currentConnectorStyle = { ...this.currentConnectorStyle, ...style };
    for (const c of this.store.connectors) {
      if (this.store.selectedElements.has(c.id)) {
        c.style = { ...c.style, ...style };
      }
    }
    this.render();
  }

  setTextStyle(style: Partial<TextStyle>): void {
    const isEditing = this.textEditor.isEditing();
    const hasSelection = this.store.selectedElements.size > 0;

    if (hasSelection) this.saveState();
    this.currentTextStyle = { ...this.currentTextStyle, ...style };

    if (isEditing) {
      const editingShape = this.textEditor.getEditingShape();
      const editingTextElement = this.textEditor.getEditingTextElement();
      const editingConnector = this.textEditor.getEditingConnector();
      if (editingShape) editingShape.textStyle = { ...editingShape.textStyle, ...style };
      else if (editingTextElement) editingTextElement.textStyle = { ...editingTextElement.textStyle, ...style };
      else if (editingConnector && style.fontSize !== undefined) editingConnector.textSize = style.fontSize;
      this.textEditor.applyTextStyle(style);
    } else if (hasSelection) {
      for (const t of this.store.textElements) {
        if (this.store.selectedElements.has(t.id)) t.textStyle = { ...t.textStyle, ...style };
      }
      for (const s of this.store.shapes) {
        if (this.store.selectedElements.has(s.id)) s.textStyle = { ...s.textStyle, ...style };
      }
    }

    if (isEditing || hasSelection) this.commitChange();
    else this.render();
  }

  applyTextListAction(kind: 'ordered' | 'unordered'): void {
    if (this.textEditor.isEditing()) {
      this.textEditor.insertListPrefix(kind);
    }
  }

  applyLineFormat(type: 'normal'): void {
    this.textEditor.applyLineFormat(type);
  }

  setOnSelectionChange(callback: (hasSelection: boolean, textStyle: TextStyle | null) => void): void {
    this.selectionManager.setOnSelectionChange((hasSelection, textStyle) => {
      callback(hasSelection, textStyle);
      this.syncBrushSizeToSelection();
    });
  }

  private syncBrushSizeToSelection(): void {
    // Find selected draw shape's strokeWidth
    for (const d of this.store.drawShapes) {
      if (this.store.selectedElements.has(d.id)) {
        this.currentWidth = d.style.strokeWidth;
        this.onBrushSizeChangeCallback?.(this.currentWidth);
        return;
      }
    }
    // Find selected diagram shape's strokeWidth
    for (const s of this.store.shapes) {
      if (this.store.selectedElements.has(s.id)) {
        this.currentWidth = s.style.strokeWidth;
        this.onBrushSizeChangeCallback?.(this.currentWidth);
        return;
      }
    }
    // Find selected connector's strokeWidth
    for (const c of this.store.connectors) {
      if (this.store.selectedElements.has(c.id)) {
        this.currentWidth = c.style.strokeWidth;
        this.onBrushSizeChangeCallback?.(this.currentWidth);
        return;
      }
    }
  }

  setOnConnectorSelection(callback: (connector: Connector | null) => void): void {
    this.selectionManager.setOnConnectorSelection(callback);
  }

  setOnHistoryChange(callback: (canUndo: boolean, canRedo: boolean) => void): void {
    this.historyController.setOnHistoryChange(callback);
  }

  setOnBrushSizeChange(callback: (size: number) => void): void {
    this.onBrushSizeChangeCallback = callback;
  }

  updateSelectedConnectorStyle(style: { startArrow?: ArrowHead; endArrow?: ArrowHead; lineStyle?: 'solid' | 'dashed' | 'dotted'; strokeWidth?: number }): void {
    this.connectorManager.updateSelectedConnectorStyle(style);
  }

  toggleSelectedConnectorArc(): void {
    this.connectorManager.toggleSelectedConnectorArc();
  }

  hasSelection(): boolean {
    return this.store.selectedElements.size > 0;
  }

  getSelectedTextStyle(): TextStyle | null {
    return this.selectionManager.getSelectedTextStyle();
  }

  getSelectedStrokeWidth(): number | null {
    for (const shape of this.store.shapes) {
      if (this.store.selectedElements.has(shape.id)) {
        return shape.style.strokeWidth;
      }
    }
    for (const connector of this.store.connectors) {
      if (this.store.selectedElements.has(connector.id)) {
        return connector.style.strokeWidth;
      }
    }
    return null;
  }

  deleteSelected(): void {
    const ids = this.store.selectedElements;
    if (ids.size === 0) return;
    this.saveState();

    // Delete draw shapes and their attached text
    const drawShapeIds = new Set(this.store.drawShapes.filter(d => ids.has(d.id)).map(d => d.id));
    this.store.textElements = this.store.textElements.filter(t => !ids.has(t.id) && (!t.parentShapeId || !drawShapeIds.has(t.parentShapeId)));
    this.store.drawShapes = this.store.drawShapes.filter(d => !ids.has(d.id));
    this.store.shapes = filterOutSelection(this.store.shapes, ids);
    this.store.connectors = filterOutSelection(this.store.connectors, ids);
    this.store.strokes = this.store.strokes.filter(s => !ids.has(s.id));
    ids.clear();

    this.updatePlaceholderVisibility();
    this.selectionManager.notifySelectionChange();
    this.commitChange();
  }

  alignSelected(alignment: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom'): void {
    const selectedShapes = this.store.shapes.filter(s => this.store.selectedElements.has(s.id));
    if (selectedShapes.length < 2) return;
    const bounds = selectedShapes.map(s => s.getBounds());
    switch (alignment) {
      case 'left': { const m = Math.min(...bounds.map(b => b.x)); for (const s of selectedShapes) s.position.x = m; break; }
      case 'right': { const m = Math.max(...bounds.map(b => b.x + b.width)); for (let i = 0; i < selectedShapes.length; i++) selectedShapes[i]!.position.x = m - bounds[i]!.width; break; }
      case 'center': { const min = Math.min(...bounds.map(b => b.x)); const max = Math.max(...bounds.map(b => b.x + b.width)); const cx = (min + max) / 2; for (let i = 0; i < selectedShapes.length; i++) selectedShapes[i]!.position.x = cx - bounds[i]!.width / 2; break; }
      case 'top': { const m = Math.min(...bounds.map(b => b.y)); for (const s of selectedShapes) s.position.y = m; break; }
      case 'bottom': { const m = Math.max(...bounds.map(b => b.y + b.height)); for (let i = 0; i < selectedShapes.length; i++) selectedShapes[i]!.position.y = m - bounds[i]!.height; break; }
      case 'middle': { const min = Math.min(...bounds.map(b => b.y)); const max = Math.max(...bounds.map(b => b.y + b.height)); const cy = (min + max) / 2; for (let i = 0; i < selectedShapes.length; i++) selectedShapes[i]!.position.y = cy - bounds[i]!.height / 2; break; }
    }
    this.notifyChange();
    this.render();
  }

  distributeSelected(direction: 'horizontal' | 'vertical'): void {
    const selectedShapes = this.store.shapes.filter(s => this.store.selectedElements.has(s.id));
    if (selectedShapes.length < 3) return;
    const bounds = selectedShapes.map(s => s.getBounds());

    if (direction === 'horizontal') {
      const sorted = selectedShapes.map((s, i) => ({ shape: s, bounds: bounds[i]! })).sort((a, b) => a.bounds.x - b.bounds.x);
      const totalW = sorted.reduce((sum, item) => sum + item.bounds.width, 0);
      const firstX = sorted[0]!.bounds.x;
      const lastX = sorted[sorted.length - 1]!.bounds.x + sorted[sorted.length - 1]!.bounds.width;
      const gap = (lastX - firstX - totalW) / (sorted.length - 1);
      let cx = firstX;
      for (const item of sorted) { item.shape.position.x = cx; cx += item.bounds.width + gap; }
    } else {
      const sorted = selectedShapes.map((s, i) => ({ shape: s, bounds: bounds[i]! })).sort((a, b) => a.bounds.y - b.bounds.y);
      const totalH = sorted.reduce((sum, item) => sum + item.bounds.height, 0);
      const firstY = sorted[0]!.bounds.y;
      const lastY = sorted[sorted.length - 1]!.bounds.y + sorted[sorted.length - 1]!.bounds.height;
      const gap = (lastY - firstY - totalH) / (sorted.length - 1);
      let cy = firstY;
      for (const item of sorted) { item.shape.position.y = cy; cy += item.bounds.height + gap; }
    }
    this.notifyChange();
    this.render();
  }

  undo(): void {
    this.historyController.undo();
    this.render();
  }

  redo(): void {
    this.historyController.redo();
    this.render();
  }

  canUndo(): boolean {
    return this.historyController.canUndo();
  }

  canRedo(): boolean {
    return this.historyController.canRedo();
  }

  clear(): void {
    this.saveState();
    this.store.strokes = [];
    this.store.drawShapes = [];
    this.store.shapes = [];
    this.store.connectors = [];
    this.store.textElements = [];
    this.store.selectedElements.clear();
    this.updatePlaceholderVisibility();
    this.commitChange();
  }

  // --- State serialization ---
  getState(): string {
    // Temporarily sync active text edits
    const isEditing = this.textEditor.isEditing();
    let currentInputValue: string | null = null;
    let editingShape: Shape | null = null;
    let editingConnector: Connector | null = null;
    let editingTextElement: TextElement | null = null;

    if (isEditing) {
      currentInputValue = this.textEditor.getCurrentInputValue();
      editingShape = this.textEditor.getEditingShape();
      editingConnector = this.textEditor.getEditingConnector();
      editingTextElement = this.textEditor.getEditingTextElement();
      if (currentInputValue !== null) {
        if (editingShape) editingShape.text = currentInputValue;
        if (editingConnector) editingConnector.text = currentInputValue;
        if (editingTextElement) editingTextElement.text = currentInputValue;
      }
    }

    const elements = serializeDiagramElements(this.store.shapes, this.store.connectors, this.store.textElements);

    if (isEditing && currentInputValue !== null) {
      if (editingShape) editingShape.text = '';
      if (editingConnector) editingConnector.text = '';
      if (editingTextElement) editingTextElement.text = '';
    }

    return JSON.stringify({
      ...elements,
      strokes: this.store.strokes,
      drawShapes: this.store.drawShapes.map(s => s.toJSON()),
      drawTextElements: serializeTextElements(this.store.textElements.filter(t => t.parentShapeId && this.store.drawShapes.some(d => d.id === t.parentShapeId))),
      undoStack: this.historyController.getUndoStack(),
      redoStack: this.historyController.getRedoStack(),
      scale: this.scale,
      panX: this.panX,
      panY: this.panY,
      gridEnabled: this.gridEnabled,
      snapEnabled: this.snapEnabled,
      inverted: this.inverted,
      currentColor: this.currentColor,
      currentWidth: this.currentWidth,
      currentTool: this.currentTool,
      currentTextStyle: this.currentTextStyle,
      currentStyle: this.currentStyle,
      currentConnectorStyle: this.currentConnectorStyle,
      activeMode: this.activeMode,
      shapeCreationMode: this.shapeCreationMode,
    });
  }

  loadState(stateJson: string): void {
    this.textEditor.close();
    try {
      const state = JSON.parse(stateJson);

      // Diagram elements
      if (state.shapes !== undefined || state.connectors !== undefined || state.textElements !== undefined) {
        const { shapes, connectors, textElements } = deserializeDiagramElements({
          shapes: state.shapes ?? [],
          connectors: state.connectors ?? [],
          textElements: state.textElements ?? [],
        }, this.recognizer);
        if (state.shapes !== undefined) this.store.shapes = shapes;
        if (state.connectors !== undefined) this.store.connectors = connectors;
        if (state.textElements !== undefined) this.store.textElements = textElements;
      }

      // Draw elements
      if (state.strokes !== undefined) {
        this.store.strokes = state.strokes;
        for (const s of this.store.strokes) {
          if (!s.id) s.id = crypto.randomUUID();
        }
      }
      if (state.drawShapes !== undefined) {
        this.store.drawShapes = deserializeDrawShapes(state.drawShapes);
      }

      this.historyController.load(state.undoStack ?? [], state.redoStack ?? []);
      this.scale = state.scale ?? 1;
      this.panX = state.panX ?? 0;
      this.panY = state.panY ?? 0;
      this.stateLoaded = true;
      this.gridEnabled = state.gridEnabled ?? false;
      this.snapEnabled = state.snapEnabled ?? false;
      this.inverted = state.inverted ?? false;
      this.currentColor = state.currentColor ?? '#000000';
      this.currentWidth = state.currentWidth ?? 8;
      this.currentTextStyle = normalizeTextStyle(state.currentTextStyle);
      this.currentStyle = state.currentStyle ?? SHAPE_PRESETS['default'] ?? {};
      this.currentConnectorStyle = state.currentConnectorStyle ?? {};
      this.currentTool = state.currentTool ?? 'pen';
      this.activeMode = state.activeMode ?? 'draw';
      this.shapeCreationMode = state.shapeCreationMode ?? 'draw';
      this.store.selectedElements.clear();

      this.updatePlaceholderVisibility();
      this.connectorManager.syncConnectorEndpoints();
      this.historyController.ensureSnapshot(this.getStateSnapshot());

      this.render();
      this.historyController.notifyHistoryChange();
      this.viewport.notifyViewChange();
    } catch {
      // Invalid state, ignore
    }
  }

  getConfig(): { width: number; height: number; backgroundColor: string } {
    const rect = this.canvas.getBoundingClientRect();
    return { width: rect.width, height: rect.height, backgroundColor: '#ffffff' };
  }

  resetView(): void {
    const bounds = this.exportManager.getContentBounds();
    const rect = this.canvas.getBoundingClientRect();
    this.viewport.resetView(bounds, rect);
    this.textEditor.updatePosition();
  }

  fitToScreen(): void {
    const bounds = this.exportManager.getContentBounds();
    const rect = this.canvas.getBoundingClientRect();
    this.viewport.fitToScreen(bounds, rect);
    this.textEditor.updatePosition();
  }

  resize(_width: number, _height: number): void {
    this.render();
  }

  destroy(): void {
    this.textEditor.close();
    this.inputController.detach();
    this.resizeObserver?.disconnect();
  }
}
